import React, { useEffect, useRef, useState } from "react";

import { PlayButtonWhite } from "../../../Theme/assets/icons/commonIcons";
import styles from "./tiktokfacade.module.css";

const OEMBED_API = "/_plat/api/tiktok/oembed";

/**
 * Click-to-load placeholder for TikTok embeds. The iframe, and the request to
 * TikTok, only happens after the reader clicks. The thumbnail comes from our
 * oEmbed api and is only requested once the placeholder is on screen. If it
 * can't be loaded we keep the plain placeholder.
 */
const TikTokFacade: React.FC<{
  videoId?: string;
  title?: string;
  onClick: () => void;
  aspectRatio: number;
}> = ({ videoId, title, onClick, aspectRatio }) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string>();

  useEffect(() => {
    const button = buttonRef.current;
    if (!videoId || !button || typeof IntersectionObserver === "undefined") {
      return;
    }
    const controller = new AbortController();
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        fetch(`${OEMBED_API}?id=${encodeURIComponent(videoId)}`, {
          signal: controller.signal,
        })
          .then((response) => (response.ok ? response.json() : undefined))
          .then((data: { thumbnailUrl?: unknown } | undefined) => {
            if (typeof data?.thumbnailUrl === "string") {
              setThumbnailUrl(data.thumbnailUrl);
            }
            return;
          })
          .catch(() => undefined);
      },
      { rootMargin: "200px" },
    );
    observer.observe(button);
    return () => {
      observer.disconnect();
      controller.abort();
    };
  }, [videoId]);

  return (
    <button
      ref={buttonRef}
      className={styles.facade}
      type="button"
      style={{ aspectRatio }}
      onClick={onClick}
      aria-label={title ? `Play TikTok video: ${title}` : "Play TikTok video"}
    >
      {thumbnailUrl && (
        <img
          className={styles.thumbnail}
          src={thumbnailUrl}
          alt=""
          referrerPolicy="no-referrer"
          onError={() => setThumbnailUrl(undefined)}
        />
      )}
      <span className={styles.playIcon} aria-hidden="true">
        {PlayButtonWhite}
      </span>
      <span className={styles.label}>Play on TikTok</span>
    </button>
  );
};

export default TikTokFacade;
